import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { RxShadeSystemRulesService } from '@shared/services/rx-rules-helper/rx-shade-system-rules.service';
import { RxConfiguration } from '@shared/models/rx-configuration';
import { DentureDetailsModel } from '@shared/models/rx-models/interfaces/denture-details-model';
import { DentureDetailsState, DentureDetailsStore } from '../state/denture-details.store';

/**
 * Initialises DentureDetailsStore from shell RX exactly once when the app starts —
 * analogous to NotesService.setNotesArray() and ApplianceDetailsInitService.setApplianceDetails().
 *
 * Components call loadFromStore() on init and always get the current value,
 * even after hide/show cycles — no getLoadedRx() in components needed.
 */
@Injectable({ providedIn: 'root' })
export class DentureDetailsInitService {
	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly store: DentureDetailsStore,
		private readonly rxShadeSystemRulesService: RxShadeSystemRulesService
	) {}

	/** Reads DentureDetails from the loaded RX once and writes into DentureDetailsStore. */
	setDentureDetails(): Observable<DentureDetailsState> {
		return combineLatest([this.shellQuery.dentureDetails$, this.shellQuery.rxConfiguration$]).pipe(
			filter((latest): latest is [DentureDetailsModel, RxConfiguration] => {
				const [dentureDetails, rxConfiguration] = latest;

				return !!dentureDetails && !!rxConfiguration;
			}),
			take(1),
			map(
				([dentureDetails, rxConfiguration]): DentureDetailsState => ({
					isUpperJawChecked: dentureDetails.UpperJaw,
					isLowerJawChecked: dentureDetails.LowerJaw,
					stage: rxConfiguration.Stages.find(x => x.Id === dentureDetails.Stage) ?? null,
					mould: rxConfiguration.Moulds.find(x => x.Id === dentureDetails.Mould) ?? null,
					shadeSystem: this.shadeSystems.find(x => x.Id === dentureDetails.ShadeSystemId) ?? null,
					teethShade: dentureDetails.TeethShade || null,
					gingivalShade: rxConfiguration.DentureGingivalShades.find(x => x.Id === dentureDetails.GingivalShadeId) ?? null,
					isDentureCopyScan: dentureDetails.IsDentureCopyScan
				})
			),
			tap(state => this.store.update(state))
		);
	}

	private get shadeSystems() {
		return this.rxShadeSystemRulesService.getDefaultShadeSystemOptions();
	}
}
